/* eslint-disable @typescript-eslint/no-explicit-any */
import L from "leaflet";

import { ClusterIcon } from "@/components/Leaflet/Map/Marker/ClusterIcon";
import {
  PruneCluster,
  PruneClusterForLeaflet,
} from "@/components/Leaflet/Map/PruneCluster/PruneCluster";
import Cluster_Icon from "@images/map/cluster-icon.svg";

// Shared core for every asset/device cluster layer on the platform.
//
// There are only two real variants: one fetches its own devices
// (SelfFetchingAssetClusterLayer), the other is fed markers by the page
// (AssetClusterLayer). EVERYTHING else — clustering aggressiveness, the cluster
// icon (severity ring + motion arc), marker preparation, spiderfy distance,
// side-panel threshold and the findClusterMarker/getLatLngByKey helpers — is
// identical and lives here so the two variants can never drift apart again.

export interface AssetClusterMarkerData {
  icon: {
    options: {
      containerEl?: string;
      id?: number | string;
      type?: string;
    };
    setMarker: (marker: L.Marker) => void;
  };
  tooltip?: string;
  deviceInfo: {
    id: number | string;
    highestSeverity?: string | null;
    lastActivityState?: string;
    [key: string]: unknown;
  };
}

export interface AssetClusterOptions {
  /** Marker pane so the page controls z-order (see layerPanes). */
  pane?: string;
  /** Fire the side-panel event once a cluster holds at least this many markers. */
  panelThreshold?: number;
  /** Cluster icon image + class (defaults to the standard asset cluster icon). */
  iconUrl?: string;
  iconClassName?: string;
  /** Per-marker hook for caller-specific wiring (click handlers, etc.). */
  prepareMarker?: (leafletMarker: L.Marker, data: AssetClusterMarkerData) => void;
}

export interface AssetClusterHandle {
  pruneCluster: PruneClusterForLeaflet;
  /** Clusters seen during the last render (populated by BuildLeafletClusterIcon). */
  clusters: unknown[];
  /** Non-clustered markers keyed by device id (populated by PrepareLeafletMarker). */
  markers: Record<number | string, L.Marker>;
  findClusterMarker: (id: number | string) => L.Marker | null;
  getLatLngByKey: (key: number | string) => L.LatLng | null;
  /**
   * Toggle clustering (the layer control's "group" switch). Call ProcessView()
   * afterwards to re-render. Disabling zeroes both the grid size and the merge
   * margin so every marker stands alone.
   */
  setClusteringEnabled: (enabled: boolean) => void;
}

const getHighestSeverityAndMotion = (
  markers: { data: AssetClusterMarkerData }[]
): { highestSeverity: string | null; totalMoving: number } => {
  const severityOrder = ["REMINDER", "WARNING", "CRITICAL"];
  const movingState = ["START", "EXIT", "ON_THE_MOVE"];

  let highestSeverity: string | null = null;
  let totalMoving = 0;
  for (let i = 0; i < markers.length; i++) {
    const info = markers[i].data.deviceInfo;
    if (
      info?.highestSeverity &&
      severityOrder.indexOf(info.highestSeverity) >
        severityOrder.indexOf(highestSeverity as string)
    ) {
      highestSeverity = info.highestSeverity;
    }
    if (info?.lastActivityState && movingState.includes(info.lastActivityState)) {
      totalMoving++;
    }
  }
  return { highestSeverity, totalMoving };
};

/**
 * Build a fully-configured asset PruneCluster layer. Both the self-fetching and
 * the page-driven variants call this so their behavior stays identical.
 */
export function createAssetPruneCluster(
  opts: AssetClusterOptions = {}
): AssetClusterHandle {
  // 120/30 lifts the clusterMargin cap so the runtime marginRatio (margin / Size)
  // stays large → aggressive merging, matching across every asset map.
  const pruneCluster = new PruneClusterForLeaflet(120, 30);
  pruneCluster.options.pane = opts.pane;
  pruneCluster.spiderfier.spiderfyDistanceMultiplier = 2.2;
  if (opts.panelThreshold !== undefined) {
    pruneCluster.panelThreshold = opts.panelThreshold;
  }

  PruneCluster.Cluster.ENABLE_MARKERS_LIST = true;

  // Layer-control "group" toggle. When clustering is off, both the grid size
  // AND the merge margin must go to ~zero: the merge margin is effectively
  // screen-space (clusterMargin px, since marginRatio = clusterMargin / Size
  // scales inversely with the cell size), so shrinking only the grid would
  // still collapse markers that sit within ~clusterMargin px of each other.
  let clusteringEnabled = true;
  const enabledClusterMargin = pruneCluster.clusterMargin;
  const setClusteringEnabled = (enabled: boolean) => {
    clusteringEnabled = enabled;
    if (enabled) {
      pruneCluster.Cluster.Size = 10;
      pruneCluster.clusterMargin = enabledClusterMargin;
    } else {
      pruneCluster.Cluster.Size = 0.0000000001;
      pruneCluster.clusterMargin = 0;
    }
  };

  const iconUrl = opts.iconUrl ?? Cluster_Icon;
  const iconClassName = opts.iconClassName ?? "cluster-svg";
  const clusters: unknown[] = [];
  const markers: Record<number | string, L.Marker> = {};

  pruneCluster.BuildLeafletClusterIcon = function (cluster: any) {
    clusters.push(cluster);

    const clusterMarkers = cluster.GetClusterMarkers() as {
      data: AssetClusterMarkerData;
    }[];
    const _length = clusterMarkers.length;

    // Grid size scales with the layer's TOTAL marker count (perf guard for big
    // fleets). Cluster.Size is global to the layer, so it must not depend on
    // the cluster whose icon happens to be rendered: the old per-cluster count
    // meant whichever icon was built last set the grid for the next clustering
    // pass — non-deterministic clustering and overlapping cluster icons.
    if (clusteringEnabled) {
      const totalMarkers = pruneCluster.Cluster.GetPopulation();
      if (totalMarkers < 500) {
        pruneCluster.Cluster.Size = 10;
      } else if (totalMarkers < 5000) {
        pruneCluster.Cluster.Size = 80;
      } else {
        pruneCluster.Cluster.Size = 120;
      }
    }

    const obj = getHighestSeverityAndMotion(clusterMarkers);

    return ClusterIcon({
      count: _length,
      iconUrl,
      className: iconClassName,
      alertSeverity: obj.highestSeverity,
      motion: (obj.totalMoving / _length) * 100,
    }) as unknown as L.Icon;
  };

  pruneCluster.PrepareLeafletMarker = function (
    leafletMarker: L.Marker,
    rawData: unknown
  ) {
    const data = rawData as AssetClusterMarkerData;
    data.icon.setMarker(leafletMarker);
    leafletMarker.setIcon(data.icon as unknown as L.Icon);
    // Same pane as the cluster icons so the whole layer sits at one z-level.
    if (opts.pane) {
      leafletMarker.options.pane = opts.pane;
    }
    // Expose the prefetched device so hover handlers can reuse its asset
    // type/tags (getBasicById doesn't return those).
    (leafletMarker as any).deviceInfo = data.deviceInfo;

    if (data.tooltip) {
      leafletMarker.unbindTooltip();
      leafletMarker.bindTooltip(data.tooltip, {
        permanent: true,
        direction: "top",
        className: "transparent-tooltip",
        offset: [0, -8],
      });
    }

    markers[data.deviceInfo.id] = leafletMarker;

    opts.prepareMarker?.(leafletMarker, data);
  };

  const findClusterMarker = (id: number | string): L.Marker | null => {
    const _clusters = (pruneCluster.Cluster as unknown as {
      _clusters: Array<{
        data: { _leafletMarker: L.Marker };
        GetClusterMarkers: () => { data: AssetClusterMarkerData }[];
      }>;
    })._clusters;

    for (let i = 0; i < _clusters.length; i++) {
      const cluster = _clusters[i];
      const clusterMarkers = cluster.GetClusterMarkers();
      for (let j = 0; j < clusterMarkers.length; j++) {
        if (id === clusterMarkers[j].data.deviceInfo.id) {
          return cluster.data._leafletMarker;
        }
      }
    }
    return null;
  };

  const getLatLngByKey = (key: number | string): L.LatLng | null => {
    const _clusters = (pruneCluster.Cluster as unknown as {
      _clusters: Array<{
        bounds: unknown;
        _clusterMarkers: { data: AssetClusterMarkerData }[];
        data: { _leafletMarker: L.Marker & { getLatLng: () => L.LatLng } };
      }>;
    })._clusters;

    for (let i = 0; i < _clusters.length; i++) {
      const _clusterMarkers = _clusters[i]._clusterMarkers;
      for (let j = 0; j < _clusterMarkers.length; j++) {
        if (_clusterMarkers[j].data.deviceInfo.id === key) {
          const markersArea = (pruneCluster.Cluster as unknown as {
            FindMarkersInArea: (bounds: unknown) => unknown[];
          }).FindMarkersInArea(_clusters[i].bounds);
          if (markersArea.length <= 1) {
            continue;
          }
          return _clusters[i].data._leafletMarker.getLatLng();
        }
      }
    }
    return null;
  };

  return {
    pruneCluster,
    clusters,
    markers,
    findClusterMarker,
    getLatLngByKey,
    setClusteringEnabled,
  };
}
